import { useState } from "react";

export const INVESTING_TERMS = {
  portfolio:
    "A group of investments you want to understand together, such as a retirement account or an investment strategy.",
  holding: "An investment you own. Investment teams also call it a position.",
  exposure:
    "How much of your portfolio could be affected by a company, market, or event. Different holdings can share the same exposure.",
  concentration:
    "How much of your portfolio depends on one company, industry, or theme. Several different funds can still hold the same companies.",
  assumption:
    "Something you believe will remain true, such as a company continuing to grow. A what-if tests what happens if it changes.",
  scenario:
    "A possible future you can explore. In Polyfuture we call it a what-if; it is not a prediction.",
  "cost basis":
    "What you paid for an investment. You can add it to compare that cost with later values.",
  "market value":
    "What a holding is worth at a stated share price: number of shares multiplied by price.",
  notional:
    "The reference amount used to describe an investment's exposure. For a derivative, it is not necessarily its market value or the amount you could lose.",
  liquidity: "How easily an investment can be turned into cash without a large price change.",
  volatility:
    "How much prices move over time. Higher volatility means larger swings, in either direction.",
  drawdown: "A fall in value from an earlier high. A drop from $100 to $80 is a 20% drawdown.",
  correlation:
    "How closely investments have moved together. That relationship can change and does not prove that one causes the other.",
  "basis points":
    "A way to describe changes in rates. 100 basis points (100 bp or bps) equals 1 percentage point: for example, 4% to 5%.",
  ETF: "An exchange-traded fund: an investment that holds a collection of assets and trades on an exchange like a stock.",
  leverage:
    "Using borrowing or derivatives to increase exposure. It can magnify both gains and losses.",
  margin:
    "Cash or assets a lender or broker requires to support a position. A margin call asks you to provide more.",
  "long / short":
    "Long generally benefits when an investment rises in value. Short generally benefits when it falls, but can lose money if it rises.",
  "P&L":
    "Profit and loss: the gain or loss over a period or under a what-if. A modelled P&L is an estimate, not a promised outcome.",
  thesis: "Your reason for holding an investment or expecting a particular outcome.",
  "stress test":
    "An analysis of how a difficult possible change could affect your holdings. It is not a forecast that the change will happen.",
  threshold: "The level that causes an alert, such as a price falling below an amount you choose.",
  tripwire: "An alert rule: a measure, a threshold, and a schedule for checking it.",
  hysteresis:
    "A separate reset level that stops an alert switching on and off when a value stays close to its trigger.",
  falsification:
    "Evidence that would show an assumption or what-if is wrong, so it can be reconsidered.",
  provenance: "Where an answer came from: its source, date, and any calculation used.",
  dataset:
    "A saved set of results, such as share prices or company filings. Saving the original values makes an analysis possible to check later.",
  "as of":
    "The time a value describes. A newly fetched result may still contain an older or delayed observation.",
  "go live":
    "Turn on monitoring for the portfolio after checking its holdings, what-ifs, and connected alert rules. Only configured rules are watched.",
} as const;

export function InvestingHelp({
  terms,
}: {
  readonly terms?: readonly (keyof typeof INVESTING_TERMS)[];
}) {
  const [query, setQuery] = useState("");
  const entries = Object.entries(INVESTING_TERMS).filter(
    ([term, meaning]) =>
      (terms === undefined || terms.includes(term as keyof typeof INVESTING_TERMS)) &&
      `${term} ${meaning}`.toLowerCase().includes(query.trim().toLowerCase()),
  );
  return (
    <details className="investing-help">
      <summary>Help with investing terms</summary>
      <label>
        Find a term
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="For example, exposure or basis points"
        />
      </label>
      <dl>
        {entries.map(([term, meaning]) => (
          <div key={term}>
            <dt>{term}</dt>
            <dd>{meaning}</dd>
          </div>
        ))}
      </dl>
      {entries.length === 0 ? (
        <p>No matching term. Ask Polyfuture to explain it in plain language.</p>
      ) : null}
    </details>
  );
}

export const INVESTOR_AUDIENCES = [
  {
    key: "individual_investor",
    name: "I invest for myself",
    description:
      "Understand your stocks and funds, explore what could change, and keep an eye on your investments.",
  },
  {
    key: "asset_manager_risk",
    name: "I manage investments for clients",
    description:
      "Research investments, test portfolio risks, and share findings with your investment team.",
  },
] as const;
